import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import type { OrderLifecycleRentRecoveryRecord } from '@/types'

interface OrderRentRecoverySectionProps {
  records?: OrderLifecycleRentRecoveryRecord[]
  locale?: string
  t: TFunction
  formatStatus: (status: string | undefined, translate: TFunction) => string
  formatTimestamp: (value?: string | null, locale?: string) => string | null
  renderTxCell: (value: string | null | undefined) => JSX.Element | null
  onTriggerManualCleanup?: () => void
  isTriggeringManualCleanup?: boolean
  onVerifyRecord?: (recordId: string) => void
  verifyingRecordId?: string | null
  onRetryRecord?: (recordId: string) => void
  retryingRecordId?: string | null
}

const renderValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') {
    return '--'
  }
  return String(value)
}

const readMetadataString = (metadata: Record<string, unknown> | undefined, key: string) => {
  const value = metadata?.[key]
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

const readMetadataBoolean = (metadata: Record<string, unknown> | undefined, key: string) => {
  const value = metadata?.[key]
  return typeof value === 'boolean' ? value : null
}

export const OrderRentRecoverySection = ({
  records,
  locale,
  t,
  formatStatus,
  formatTimestamp,
  renderTxCell,
  onTriggerManualCleanup,
  isTriggeringManualCleanup,
  onVerifyRecord,
  verifyingRecordId,
  onRetryRecord,
  retryingRecordId,
}: OrderRentRecoverySectionProps) => {
  if (!records || records.length === 0) {
    return (
      <VStack align="stretch" spacing={3}>
        {onTriggerManualCleanup ? (
          <HStack justify="flex-end">
            <Button
              size="sm"
              colorScheme="blue"
              onClick={onTriggerManualCleanup}
              isLoading={isTriggeringManualCleanup}
            >
              {t('orders.lifecycle.rentRecovery.runCleanup')}
            </Button>
          </HStack>
        ) : null}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">{t('orders.lifecycle.rentRecovery.noData')}</Text>
        </Alert>
      </VStack>
    )
  }

  return (
    <VStack align="stretch" spacing={4}>
      {onTriggerManualCleanup ? (
        <HStack justify="flex-end">
          <Button
            size="sm"
            colorScheme="blue"
            onClick={onTriggerManualCleanup}
            isLoading={isTriggeringManualCleanup}
          >
            {t('orders.lifecycle.rentRecovery.runCleanup')}
          </Button>
        </HStack>
      ) : null}
      {records.map(record => (
        <Box key={record.id} borderWidth="1px" borderRadius="md" p={4}>
          {(() => {
            const recipientTokenAccount = readMetadataString(
              record.metadata,
              'recipientTokenAccount'
            )
            const recipientTokenAccountCreated = readMetadataBoolean(
              record.metadata,
              'recipientTokenAccountCreated'
            )

            return (
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text fontWeight="medium">{t('orders.lifecycle.rentRecovery.record')}</Text>
              <HStack spacing={2}>
                <Badge colorScheme={record.orderClosed ? 'green' : 'orange'}>
                  {formatStatus(record.status, t)}
                </Badge>
                {onVerifyRecord ? (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => onVerifyRecord(record.id)}
                    isLoading={verifyingRecordId === record.id}
                  >
                    {t('orders.lifecycle.rentRecovery.verify')}
                  </Button>
                ) : null}
                {onRetryRecord ? (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => onRetryRecord(record.id)}
                    isLoading={retryingRecordId === record.id}
                  >
                    {t('orders.lifecycle.rentRecovery.retry')}
                  </Button>
                ) : null}
              </HStack>
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
              <Text>
                {t('orders.lifecycle.rentRecovery.mode')}: {renderValue(record.mode)}
              </Text>
              <Text>
                {t('orders.lifecycle.rentRecovery.triggerSource')}: {renderValue(record.triggerSource)}
              </Text>
              <Text>
                {t('orders.lifecycle.rentRecovery.orderClosed')}: {record.orderClosed ? t('common.yes', 'Yes') : t('common.no', 'No')}
              </Text>
              <Text>
                {t('orders.lifecycle.rentRecovery.verificationStatus')}:{' '}
                {renderValue(record.verification?.status)}
              </Text>
            </SimpleGrid>
            <Divider />
            <VStack align="stretch" spacing={2}>
              <HStack spacing={3} align="flex-start">
                <Text fontSize="sm" fontWeight="medium" minW="140px">
                  {t('orders.lifecycle.txHash')}
                </Text>
                {renderTxCell(record.tx?.hash ?? undefined)}
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
                <Text>
                  {t('orders.lifecycle.rentRecovery.slot')}: {renderValue(record.tx?.slot)}
                </Text>
                <Text>
                  {t('orders.lifecycle.rentRecovery.retryCount')}: {renderValue(record.retryCount)}
                </Text>
                <Text>
                  {t('orders.lifecycle.rentRecovery.paymentEntryCountBefore')}:{' '}
                  {renderValue(record.paymentEntries?.countBefore)}
                </Text>
                <Text>
                  {t('orders.lifecycle.rentRecovery.paymentEntriesClosed')}:{' '}
                  {renderValue(record.paymentEntries?.closedAfter)}
                </Text>
                <Text>
                  {t('orders.lifecycle.rentRecovery.actualVaultBefore')}:{' '}
                  {renderValue(record.balances?.actualVaultBefore)}
                </Text>
                <Text>
                  {t('orders.lifecycle.rentRecovery.actualVaultAfter')}:{' '}
                  {renderValue(record.balances?.actualVaultAfter)}
                </Text>
                <Text>
                  {t('orders.lifecycle.rentRecovery.trackedRemainingBefore')}:{' '}
                  {renderValue(record.balances?.trackedRemainingBefore)}
                </Text>
                <Text>
                  {t('orders.lifecycle.rentRecovery.trackedRemainingAfter')}:{' '}
                  {renderValue(record.balances?.trackedRemainingAfter)}
                </Text>
                <Text>
                  {t('orders.lifecycle.rentRecovery.recipientTokenAccount')}:{' '}
                  {renderValue(recipientTokenAccount)}
                </Text>
                <Text>
                  {t('orders.lifecycle.rentRecovery.recipientTokenAccountCreated')}:{' '}
                  {recipientTokenAccountCreated === null
                    ? '--'
                    : recipientTokenAccountCreated
                      ? t('common.yes', 'Yes')
                      : t('common.no', 'No')}
                </Text>
              </SimpleGrid>
              {record.triggerReason && (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.rentRecovery.triggerReason')}
                  </Text>
                  <Text fontSize="sm">{record.triggerReason}</Text>
                </HStack>
              )}
              {record.verification?.notes && (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.rentRecovery.verificationNotes')}
                  </Text>
                  <Text fontSize="sm">{record.verification.notes}</Text>
                </HStack>
              )}
              {record.error?.message && (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.rentRecovery.error')}
                  </Text>
                  <Text fontSize="sm">{record.error.message}</Text>
                </HStack>
              )}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
                <Text>
                  {t('orders.lifecycle.createdAt')}: {formatTimestamp(record.timestamps?.createdAt, locale) ?? '--'}
                </Text>
                <Text>
                  {t('orders.lifecycle.updatedAt')}: {formatTimestamp(record.timestamps?.updatedAt, locale) ?? '--'}
                </Text>
              </SimpleGrid>
            </VStack>
          </VStack>
            )
          })()}
        </Box>
      ))}
    </VStack>
  )
}
